/**
 * Builds a stable, value-based signature of the inputs that should invalidate a
 * broadcast preview. The composer resets its cached preview whenever this key
 * changes.
 *
 * Why a value signature instead of the raw values as effect dependencies:
 * several of these inputs are recomputed into fresh references on every render
 * (`geographyOverlap` comes from `Array.filter`, `audienceSelection` from a
 * derived object). Used directly as React effect deps they change *identity*
 * every render, which fired the reset effect on every render and wiped a
 * just-computed preview — permanently disabling the "Send broadcast" button.
 * Comparing by serialized value makes the reset fire only on a real change.
 */
export function broadcastPreviewInvalidationKey(input: {
  templateId: string;
  senderKey?: string;
  audienceSelection: unknown;
  parameters: unknown;
  headerMedia: unknown;
  geographyOverlap: readonly string[];
  includeNotOptedIn?: boolean;
  ratingFilter?: { stars: readonly number[]; unrated: boolean };
}): string {
  return JSON.stringify({
    templateId: input.templateId,
    senderKey: input.senderKey ?? "",
    audienceSelection: input.audienceSelection ?? null,
    parameters: input.parameters ?? null,
    headerMedia: input.headerMedia ?? null,
    // Order-insensitive: overlap is a set, not a sequence.
    geographyOverlap: [...(input.geographyOverlap ?? [])].sort(),
    // Toggling include-not-opted-in changes eligibility, so the preview must
    // recompute when it flips.
    includeNotOptedIn: input.includeNotOptedIn ?? false,
    // RSB rating refinement: normally already reflected in audienceSelection,
    // kept explicit so a preview can never survive a rating toggle.
    ratingFilter: input.ratingFilter
      ? {
          stars: [...input.ratingFilter.stars].sort(),
          unrated: input.ratingFilter.unrated,
        }
      : null,
  });
}
